import React, { useEffect, useState } from "react";
import popupimage from "../../assets/images/Confetti-thank-you-page.png";
import popupimage2 from "../../assets/images/Group 8.svg";
import Loader from "../utils/Loader";
import Popup from "../utils/Popup";
import Toaster from "../utils/Toaster";
import { getCart, clearCart, removeCartItem } from "../../utils/cart";
import {
  useCreatePaymentOrderMutation,
  useVerifyPaymentMutation,
  useRecoverPaymentMutation,
  useRecordShipmentPaymentMutation,
  useSendOTPMutation,
  useVerifyOTPMutation,
  useRegisterMutation,
  useCreateUnifiedShipmentMutation,
  usePlaceCheckoutMutation,
  useAbandonCheckoutMutation,
  useGetPointsQuery,
  useGetAddressesQuery,
  useCreateAddressMutation,
  useUpdateAddressMutation,
  apiSlice,
} from "../../store/apiSlice";
import {
  buildCheckoutSwagpacks,
  newPlaceIdempotencyKey,
  pollCheckoutActivated,
  razorpayLocalCheckoutOptions,
} from "../../utils/checkoutPlace";
import { useDispatch } from "react-redux";
import {
  Box,
  Card as MuiCard,
  CardContent,
  TextField,
  Grid,
  Paper,
  Stepper,
  Step,
  StepLabel,
  IconButton,
  Container,
  Checkbox,
  FormControlLabel,
  CircularProgress,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import EditIcon from "@mui/icons-material/Edit";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import PaymentIcon from "@mui/icons-material/Payment";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import LockIcon from "@mui/icons-material/Lock";
import HomeOutlined from "@mui/icons-material/HomeOutlined";
import BusinessOutlined from "@mui/icons-material/BusinessOutlined";
import Add from "@mui/icons-material/Add";
import { AppTypography } from "../common/Typography";
import AppButton from "../common/Button";
import { Card } from "../common/Card";
import { useTheme } from "../../theme/ThemeContext";
import { useAuth } from "../../context/AuthContext";
import OTPInput from "../common/OTPInput";
import { useStoreInfo } from "../../context/StoreInfoContext";
import { usePreserveStoreIdNavigate } from "../utils/usePreserveStoreIdNavigate";
import AddressModal from "../common/AddressModal";

// Styled components
const StyledCard = styled(MuiCard)(({ theme }) => ({
  borderRadius: 8,
  backgroundColor: "#FFFFFF",
  boxShadow: "0 4px 20px rgba(0, 0, 0, 0.1)",
  transition: "all 0.3s ease",
  "&:hover": {
    boxShadow: "0 8px 30px rgba(0, 0, 0, 0.15)",
  },
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
  "& .MuiOutlinedInput-root": {
    borderRadius: 12,
    fontSize: "0.9rem",
    alignItems: "center",
    "&:hover fieldset": {
      borderColor: theme?.app_button_color,
    },
    // Handle autofill styling
    "&:-webkit-autofill": {
      "-webkit-box-shadow": "0 0 0 100px white inset",
      "-webkit-text-fill-color": theme?.text_color,
    },
    "&:-webkit-autofill:hover": {
      "-webkit-box-shadow": "0 0 0 100px white inset",
    },
    "&:-webkit-autofill:focus": {
      "-webkit-box-shadow": "0 0 0 100px white inset",
    },
  },
  "& .MuiInputLabel-root": {
    fontSize: "0.9rem",
  },
  // Style the required field asterisk
  "& .MuiInputLabel-asterisk": {
    color: "#d32f2f", // Red color for required field indicator
    fontSize: "0.9rem",
  },
  // Reduce padding for multiline text fields
  "& .MuiOutlinedInput-inputMultiline": {
    padding: "8px 10px",
    fontSize: "0.9rem",
  },
}));

const StyledButton = styled(AppButton)(({ theme }) => ({
  borderRadius: 8,
  fontSize: "0.9rem",
  padding: "12px 24px",
  boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
  backgroundColor: theme?.app_button_color,
  color: theme?.app_button_text_color,
  "&:hover": {
    boxShadow: "0 6px 20px rgba(0, 0, 0, 0.25)",
    backgroundColor: theme?.app_hover_button_color,
    opacity: 0.9,
  },
}));

const AddressDisplayCard = styled(Paper)(({ theme }) => ({
  padding: 24,
  borderRadius: 8,
  backgroundColor: "#FFFFFF",
  border: `1px solid ${theme?.divider}`,
}));

const OrderSummaryCard = styled(Card)(({ theme }) => ({
  padding: 24,
  height: "fit-content",
  position: "sticky",
  top: 80,
  backgroundColor: "#FFFFFF",
  border: `1px solid ${theme?.divider}`,
  borderRadius: 8,
  boxShadow: "0 4px 20px rgba(0, 0, 0, 0.1)",
}));

/** Maps API address (StoreUserAddress) to checkout address state */
const mapApiAddressToCheckoutState = (addr) => {
  const hasSplitName = addr?.first_name || addr?.last_name;
  const nameParts = (addr?.name || "").trim().split(/\s+/);
  const first_name = hasSplitName ? addr?.first_name || "" : nameParts[0] || "";
  const last_name = hasSplitName
    ? addr?.last_name || ""
    : nameParts.slice(1).join(" ") || "";
  return {
    first_name,
    last_name,
    calling_code: 91,
    phone: addr?.phone_number ?? "",
    email: addr?.email_id ?? "",
    address1: addr?.address_line_1 ?? "",
    address2: addr?.address_line_2 ?? "",
    address3: addr?.address_line_3 ?? "",
    landmark: addr?.landmark ?? "",
    city: addr?.city ?? "",
    state: addr?.state ?? "",
    country: addr?.country ?? "",
    pincode: addr?.zipcode ?? "",
  };
};

const Checkout = () => {
  const [displayEditForm, setDisplayEditForm] = useState(false);
  const [paymentVerified, setPaymentVerified] = useState(false);
  const [activeStep, setActiveStep] = useState(0);
  const [redirectCountdown, setRedirectCountdown] = useState(3);
  const [isProcessingPayment, setIsProcessingPayment] = useState(false);
  const [isConfirmingOrder, setIsConfirmingOrder] = useState(false);

  // OTP verification states
  const [showOTPPopup, setShowOTPPopup] = useState(false);
  const [otp, setOtp] = useState("");
  const [isUserRegistered, setIsUserRegistered] = useState(false);
  const [userEmail, setUserEmail] = useState("");
  const [isVerifyingUser, setIsVerifyingUser] = useState(false);

  const theme = useTheme();
  const navigate = usePreserveStoreIdNavigate();

  // Helper function to check if a size is onesize (case-insensitive)
  const isOnesize = (size) => {
    if (!size) return false;
    const sizeLower = size.toLowerCase().trim();
    return (
      sizeLower === "onesize" ||
      sizeLower === "one size" ||
      sizeLower === "one-size"
    );
  };
  const dispatch = useDispatch();
  const { user: userData, login } = useAuth();
  const [createUnifiedShipment, { isLoading: isCreatingUnifiedShipment }] =
    useCreateUnifiedShipmentMutation();
  const [placeCheckout, { isLoading: isPlacingCheckout }] =
    usePlaceCheckoutMutation();
  const [abandonCheckout] = useAbandonCheckoutMutation();
  const [createPaymentOrder, { isLoading: isCreatingOrder }] =
    useCreatePaymentOrderMutation();
  const [verifyPayment, { isLoading: isVerifyingPayment }] =
    useVerifyPaymentMutation();
  const [recoverPayment, { isLoading: isRecoveringPayment }] =
    useRecoverPaymentMutation();
  const [recordShipmentPayment] = useRecordShipmentPaymentMutation();
  const [sendOTP, { isLoading: isSendingOTP }] = useSendOTPMutation();
  const [verifyOTP, { isLoading: isVerifyingOTP }] = useVerifyOTPMutation();
  const [register, { isLoading: isRegistering }] = useRegisterMutation();

  // Check if customer has inventory from store info
  const { storeInfo, customerId, storeId } = useStoreInfo();
  const userId = userData?.id ?? userData?.user_id;
  const { data: pointsData } = useGetPointsQuery(
    { storeId, customerId, userId },
    { skip: !userId || !customerId },
  );
  const {
    data: addressesData,
    refetch: refetchAddresses,
    isLoading: isLoadingAddresses,
    isFetching: isFetchingAddresses,
  } = useGetAddressesQuery(
    { storeId, customerId, userId },
    { skip: !userId || !customerId },
  );
  const [createAddress, { isLoading: isCreatingAddress }] =
    useCreateAddressMutation();
  const [updateAddress] = useUpdateAddressMutation();
  const points = pointsData?.result?.points ?? 0;
  const [usePoints, setUsePoints] = useState(false);
  const POINTS_TO_RUPEE = 1;

  const rawAddressList =
    addressesData?.result ?? addressesData?.data ?? addressesData;
  const savedAddresses = Array.isArray(rawAddressList) ? rawAddressList : [];
  const hasAddresses = savedAddresses.length > 0;
  // Show loading when: user is logged in and we're fetching addresses, OR user is logged in but customerId isn't ready yet (prevents form flash)
  const isLoadingAddressSection =
    !!userId && (!customerId || isLoadingAddresses || isFetchingAddresses);
  const [selectedAddressId, setSelectedAddressId] = useState(null);
  const [addressModalOpen, setAddressModalOpen] = useState(false);
  const [editAddressInCheckout, setEditAddressInCheckout] = useState(null);

  const [cart, setCart] = useState([]);
  const [loading, setLoading] = useState(true);
  const [totalPrice, setTotalPrice] = useState(0);

  const pointsValue = Math.min(points * POINTS_TO_RUPEE, totalPrice);
  const pointsDiscount = usePoints && userId ? pointsValue : 0;
  const totalAfterPoints = totalPrice - pointsDiscount;

  const [address, setAddress] = useState({
    first_name: "",
    last_name: "",
    calling_code: +91,
    phone: "",
    email: "",
    address1: "",
    address2: "",
    address3: "",
    landmark: "",
    city: "",
    state: "",
    country: "",
    pincode: "",
  });

  // Load cart from sessionStorage
  useEffect(() => {
    setLoading(true);
    const cartData = getCart();
    setCart(cartData);
    setLoading(false);
  }, []);

  // Update total price when cart changes
  useEffect(() => {
    const total = cart?.reduce(
      (sum, item) => sum + item.selected_quantity * (item.selling_price || 0),
      0,
    );
    setTotalPrice(total);
  }, [cart]);

  // Handle automatic redirect after successful payment
  useEffect(() => {
    if (activeStep === 2 && paymentVerified) {
      const timer = setInterval(() => {
        setRedirectCountdown((prev) => {
          if (prev <= 1) {
            clearCart();
            // Invalidate cache to force refetch of products and packs
            dispatch(apiSlice.util.resetApiState());
            navigate("/");
            return 0;
          }
          return prev - 1;
        });
      }, 1000);

      return () => clearInterval(timer);
    }
  }, [activeStep, paymentVerified, navigate, dispatch]);

  useEffect(() => {
    formattingData(cart);
  }, [cart]);

  // Pre-populate address when user has saved addresses
  useEffect(() => {
    if (hasAddresses && savedAddresses.length > 0 && !displayEditForm) {
      const defaultAddr =
        savedAddresses.find((a) => a.is_default) || savedAddresses[0];
      setSelectedAddressId(defaultAddr.id);
      setAddress((prev) => ({
        ...prev,
        ...mapApiAddressToCheckoutState(defaultAddr),
      }));
    }
  }, [hasAddresses, savedAddresses, displayEditForm]);

  const handleContinueWithSelectedAddress = () => {
    if (!selectedAddressId || !hasAddresses) return;
    const addr = savedAddresses.find((a) => a.id === selectedAddressId);
    if (!addr) return;
    setAddress((prev) => ({
      ...prev,
      ...mapApiAddressToCheckoutState(addr),
    }));
    if (storeInfo?.store?.is_public === 1 && !userData) {
      handlePublicStoreCheckout();
    } else {
      setActiveStep(1);
      setDisplayEditForm(true);
    }
  };

  const handleAddressModalSubmit = async (formData) => {
    if (!userId || !customerId) return;
    const apiPayload = {
      first_name: formData.firstName || "",
      last_name: formData.lastName || "",
      email_id: formData.email,
      phone_number: formData.phone,
      address_line_1: formData.address1,
      address_line_2: formData.address2 || undefined,
      address_line_3: formData.address3 || undefined,
      landmark: formData.landmark || undefined,
      city: formData.city,
      state: formData.state,
      country: formData.country,
      zipcode: formData.pincode || formData.zip,
      address_type: formData.type,
      is_default: formData.isDefault,
    };
    try {
      if (editAddressInCheckout) {
        await updateAddress({
          storeId,
          addressId: editAddressInCheckout.id,
          customerId,
          userId,
          ...apiPayload,
        }).unwrap();
        Toaster.show("Address updated successfully", "success");
      } else {
        await createAddress({
          userId,
          storeId,
          customerId,
          ...apiPayload,
        }).unwrap();
        Toaster.show("Address added successfully", "success");
      }
      await refetchAddresses();
      setAddressModalOpen(false);
      setEditAddressInCheckout(null);
    } catch (err) {
      const msg =
        err?.data?.message || err?.message || "Failed to save address";
      Toaster.show(msg, "error");
      throw err;
    }
  };

  function clearStoreAndNavigate() {
    clearCart();
    // Invalidate cache to force refetch of products and packs
    dispatch(apiSlice.util.resetApiState());
    navigate("/");
  }

  function formattingData(cart) {
    let length = cart.length;
    let local = [];
    for (let i = 0; i < length; i++) {
      let product = {};
      product.sku = cart[i]?.sku;
      product.quantity = cart[i]?.selected_quantity;
      local.push(product);
    }
    // setLocalCart(local); // This line was removed as per the edit hint
  }

  function handleChange(evt) {
    const name = evt.target.name;
    const value = evt.target.value;
    setAddress({
      ...address,
      [name]: value,
    });
  }

  function editForm() {
    setDisplayEditForm(false);
    setActiveStep(0);
  }

  async function handleFormSubmit(e) {
    e.preventDefault();

    // Validation checks
    const requiredFields = [
      { field: "first_name", message: "Please enter your first name" },
      { field: "last_name", message: "Please enter your last name" },
      { field: "address1", message: "Please enter address" },
      { field: "city", message: "Fill all required fields" },
      { field: "state", message: "Please enter state name" },
      { field: "country", message: "Please enter country name" },
      { field: "pincode", message: "Post Code can not be empty" },
    ];

    for (const { field, message } of requiredFields) {
      if (!address[field]) {
        Toaster.show(message, "error");
        return;
      }
    }

    if (
      !address.phone ||
      address.phone.length < 5 ||
      address.phone.length > 17
    ) {
      Toaster.show("Please enter a valid phone number", "error");
      return;
    }

    if (!address.email) {
      Toaster.show("Please enter a valid email", "error");
      return;
    }

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(address.email)) {
      Toaster.show("Please enter a valid email address", "error");
      return;
    }

    // Check if store is public and user is not logged in
    if (storeInfo?.store?.is_public === 1 && !userData) {
      handlePublicStoreCheckout();
    } else {
      // Save address via API when user is logged in and has no saved addresses
      if (userId && customerId && !hasAddresses) {
        try {
          await createAddress({
            userId,
            storeId,
            customerId,
            first_name: address.first_name,
            last_name: address.last_name,
            name: `${address.first_name} ${address.last_name}`.trim(),
            email_id: address.email,
            phone_number: address.phone,
            address_line_1: address.address1,
            address_line_2: address.address2 || undefined,
            address_line_3: address.address3 || undefined,
            landmark: address.landmark || undefined,
            city: address.city,
            state: address.state,
            country: address.country,
            zipcode: address.pincode,
            address_type: "home",
            is_default: true,
          }).unwrap();
          await refetchAddresses();
        } catch (err) {
          const msg =
            err?.data?.message || err?.message || "Failed to save address";
          Toaster.show(msg, "error");
          return;
        }
      }
      setActiveStep(1);
      setDisplayEditForm(true);
    }
  }

  // Handle OTP verification for public stores
  const handlePublicStoreCheckout = async () => {
    try {
      setIsVerifyingUser(true);
      setUserEmail(address.email);

      // First, try to send OTP to check if user exists
      await sendOTP({ customerId, storeId, email: address.email }).unwrap();

      // If OTP sent successfully, user exists
      setIsUserRegistered(true);
      setShowOTPPopup(true);
    } catch (error) {
      // If user doesn't exist, register them first
      try {
        const userData = {
          firstName: address.first_name,
          lastName: address.last_name,
          email: address.email,
          phone: address.phone,
        };

        await register({ customerId, storeId, ...userData }).unwrap();

        // After registration, send OTP
        await sendOTP({ customerId, storeId, email: address.email }).unwrap();

        setIsUserRegistered(false);
        setShowOTPPopup(true);
      } catch (registerError) {
        Toaster.show(
          registerError.data?.message ||
          "Failed to register user. Please try again.",
          "error",
        );
      }
    } finally {
      setIsVerifyingUser(false);
    }
  };

  // Handle OTP verification
  const handleOTPVerification = async () => {
    if (!otp || otp.length !== 6) {
      Toaster.show("Please enter a valid 6-character OTP", "error");
      return;
    }

    try {
      const result = await verifyOTP({
        customerId,
        storeId,
        email: userEmail,
        otp: otp,
      }).unwrap();

      if (result.result?.user) {
        // Create user data for login
        const userData = result.result?.user;
        // Log the user in
        login(userData, result.result?.token);

        // OTP verified successfully
        setShowOTPPopup(false);
        setOtp("");
        setActiveStep(1);
        setDisplayEditForm(true);

        Toaster.show(
          result.message || "Login successful! OTP verified.",
          "success",
        );
      }

      Toaster.show(
        result.message || "Login successful! OTP verified.",
        "success",
      );
    } catch (error) {
      Toaster.show(
        error.data?.message || "Invalid OTP. Please try again.",
        "error",
      );
    }
  };

  // Handle OTP resend
  const handleResendOTP = async () => {
    try {
      await sendOTP({ customerId, storeId, email: userEmail }).unwrap();
      Toaster.show("OTP sent successfully!", "success");
    } catch (error) {
      Toaster.show(
        error.data?.message || "Failed to send OTP. Please try again.",
        "error",
      );
    }
  };

  function loadScript(src) {
    return new Promise((resolve) => {
      const script = document.createElement("script");
      script.src = src;
      script.onload = () => {
        resolve(true);
      };
      script.onerror = () => {
        resolve(false);
      };
      document.body.appendChild(script);
    });
  }

  /**
   * Releases the hold when Razorpay is dismissed or setup fails after place.
   * Cart stays in sessionStorage. Next Pay is a new place.
   *
   * @param {string|null} placeId - Place returned by POST /checkout/.../place.
   * @returns {Promise<void>}
   */
  async function abandonPlacedCheckout(placeId) {
    if (!placeId || !userId || !customerId) return;
    try {
      await abandonCheckout({
        customerId,
        userId,
        storeId,
        place_id: placeId,
      }).unwrap();
    } catch (error) {
      console.error("Failed to abandon checkout:", error);
    }
  }

  /**
   * Address + swagpacks shared by place (paid) and unified (zero-payable).
   *
   * @param {number} pointsRedeemed - Points applied at submit.
   * @returns {object} Body fields for place / unified.
   */
  function checkoutAddressPayload(pointsRedeemed) {
    return {
      first_name: address.first_name,
      last_name: address.last_name,
      email: address.email,
      calling_code: address.calling_code.toString(),
      phone: address.phone,
      address1: address.address1,
      address2: address.address2 || "",
      address3: address.address3 || "",
      landmark: address.landmark || "",
      city: address.city,
      state: address.state,
      country: address.country,
      pincode: address.pincode,
      user_id: userData?.id || 0,
      swagpacks: buildCheckoutSwagpacks(cart),
      points_redeemed: Number(pointsRedeemed) || 0,
    };
  }

  function markPaidCheckoutSuccess() {
    setPaymentVerified(true);
    setActiveStep(2);
    setTimeout(() => {
      clearCart();
    }, 5000);
  }

  async function handleSubmit(e) {
    e.preventDefault();

    if (cart.length === 0) {
      Toaster.show("Your cart is empty", "error");
      return;
    }

    try {
      const cartTotal = cart?.reduce(
        (sum, item) =>
          sum + (item.selected_quantity || 0) * (item.selling_price || 0),
        0,
      );

      setTotalPrice(cartTotal);
      const pointsDiscountAtSubmit =
        usePoints && userId ? Math.min(points * POINTS_TO_RUPEE, cartTotal) : 0;
      const paymentAmount = cartTotal - pointsDiscountAtSubmit;

      if (paymentAmount <= 0) {
        await createShipmentAfterPayment(Number(pointsDiscountAtSubmit));
        return;
      }

      const snapshot = {
        store_id: storeId ?? storeInfo?.store?.id ?? null,
        customer_id: customerId,
        user_id: userId,
        cart,
        address,
        use_points: !!usePoints,
        points_balance: points,
        points_redeemed: Number(pointsDiscountAtSubmit),
        cart_total: cartTotal,
        amount_due: paymentAmount,
        currency_code: storeInfo?.store?.currency_code || "INR",
        captured_at: new Date().toISOString(),
      };

      const placed = await placeCheckout({
        customerId,
        userId,
        storeId,
        idempotency_key: newPlaceIdempotencyKey(),
        ...checkoutAddressPayload(pointsDiscountAtSubmit),
        snapshot,
      }).unwrap();

      const placeId = placed?.result?.place_id;
      if (!placeId) {
        Toaster.show("Could not start checkout. Please try again.", "error");
        return;
      }

      let paymentOrderResponse;
      try {
        paymentOrderResponse = await createPaymentOrder({
          amount: paymentAmount,
          customer_id: customerId,
          currency_code: storeInfo?.store?.currency_code || "INR",
          place_id: placeId,
          checkout_snapshot: snapshot,
        }).unwrap();
      } catch (createOrderError) {
        await abandonPlacedCheckout(placeId);
        throw createOrderError;
      }

      const res = await loadScript(
        "https://checkout.razorpay.com/v1/checkout.js",
      );
      if (!res) {
        await abandonPlacedCheckout(placeId);
        Toaster.show("Razorpay SDK failed to load", "error");
        return;
      }

      const orderDetails = paymentOrderResponse.result;
      let settled = false;
      const options = {
        key: orderDetails.key_id,
        amount: orderDetails.amount,
        currency: orderDetails.currency,
        name: "Swag Store",
        description: "Payment for your order",
        image: "https://example.com/your_logo",
        order_id: orderDetails.order_id,
        handler: function (response) {
          settled = true;
          handlePaymentSuccess(
            response.razorpay_payment_id,
            response.razorpay_order_id,
            response.razorpay_signature,
            Number(pointsDiscountAtSubmit),
            placeId,
          );
        },
        prefill: {
          name: `${address.first_name} ${address.last_name}`,
          email: address.email,
          contact: address.phone,
        },
        notes: {
          address: `${address.address1}, ${address.city}, ${address.state}`,
        },
        theme: {
          color: theme?.app_button_color || "#667eea",
        },
        modal: {
          ondismiss: function () {
            if (settled) return;
            settled = true;
            abandonPlacedCheckout(placeId);
            Toaster.show("Payment cancelled", "warning");
          },
        },
        ...razorpayLocalCheckoutOptions(),
      };

      const paymentObject = new window.Razorpay(options);
      paymentObject.open();
    } catch (error) {
      Toaster.show(
        error.data?.message || "Payment setup failed. Please try again.",
        "error",
      );
    }
  }

  async function handlePaymentSuccess(
    razorpay_payment_id,
    razorpay_order_id,
    razorpay_signature,
    points_redeemed,
    placeId,
  ) {
    if (isProcessingPayment) {
      return;
    }

    setIsProcessingPayment(true);
    setIsConfirmingOrder(true);

    try {
      try {
        const verificationResult = await verifyPayment({
          razorpay_payment_id,
          razorpay_order_id,
          razorpay_signature,
        }).unwrap();

        if (verificationResult?.success === false) {
          Toaster.show(
            "Payment verification failed. Please contact support.",
            "error",
          );
          return;
        }
      } catch (verificationError) {
        console.error("Payment verification error:", verificationError);
        const recovered = await recoverCapturedPayment(
          razorpay_payment_id,
          razorpay_order_id,
        );
        if (!recovered) {
          await abandonPlacedCheckout(placeId);
          return;
        }
      }

      await pollCheckoutActivated(razorpay_order_id);
      markPaidCheckoutSuccess();
    } catch (error) {
      console.error("Payment processing failed:", error);
      Toaster.show(
        error?.data?.message ||
          error?.message ||
          "Payment processing failed. Please contact support.",
        "error",
      );
    } finally {
      setIsProcessingPayment(false);
      setIsConfirmingOrder(false);
    }
  }

  /**
   * Saves the payment row when verify failed but Razorpay shows captured.
   * Does not create the order — the webhook / poll still activates.
   *
   * @param {string} paymentId - Razorpay payment id.
   * @param {string} orderId - Razorpay order id.
   * @returns {Promise<boolean>} True when the payment is captured.
   */
  async function recoverCapturedPayment(paymentId, orderId) {
    try {
      const statusResponse = await fetch(
        `${process.env.REACT_APP_URL}/payment/status/${paymentId}`,
        { headers: { "x-platform": "web" } },
      );
      const statusData = await statusResponse.json();

      if (statusData.success && statusData.result?.status === "captured") {
        await recoverPayment({
          payment_id: paymentId,
          order_id: orderId,
        }).unwrap();
        return true;
      }

      Toaster.show(
        "Payment was not successful. You have not been charged.",
        "error",
      );
      return false;
    } catch (recoveryError) {
      console.error("Payment recovery failed:", recoveryError);
      Toaster.show(
        recoveryError?.data?.message ||
          "Payment recovery failed. Please contact support with payment ID: " +
            paymentId,
        "error",
      );
      return false;
    }
  }

  /**
   * Zero-payable path: no Razorpay, so unified still captures and activates in one request.
   *
   * @param {number} points_redeemed - Points covering the cart.
   * @returns {Promise<void>}
   */
  async function createShipmentAfterPayment(points_redeemed = null) {
    try {
      const result = await createUnifiedShipment({
        customerId,
        storeId,
        ...checkoutAddressPayload(points_redeemed),
      }).unwrap();

      if (result?.result?.shipmentIds?.length > 0) {
        try {
          await recordShipmentPayment({
            user_id: userId,
            storeId,
            customerId,
            shipment_ids: result?.result?.shipmentIds || [],
            rzp_order_id: null,
            points_redeemed: Number(points_redeemed),
          }).unwrap();
        } catch (paymentRecordError) {
          console.error(
            "Failed to record shipment payment:",
            paymentRecordError,
          );
          Toaster.show(
            "Order placed but points could not be recorded. Please contact support.",
            "error",
          );
          return;
        }
      }

      markPaidCheckoutSuccess();
    } catch (error) {
      console.error("Shipment creation failed:", error);
      Toaster.show(
        error?.data?.message || "Something went wrong. Please contact support.",
        "error",
      );
    }
  }

  const steps = [
    {
      label: "Shipping Address",
      icon: <LocalShippingIcon sx={{ color: theme?.app_button_color }} />,
    },
    {
      label: "Payment",
      icon: <PaymentIcon sx={{ color: theme?.app_button_color }} />,
    },
    {
      label: "Confirmation",
      icon: <CheckCircleIcon sx={{ color: theme?.app_button_color }} />,
    },
  ];

  if (loading) {
    return <Loader />;
  }

  if (cart.length === 0) {
    return (
      <Container>
        <Box
          sx={{
            py: 8,
            textAlign: "center",
            ...theme?.background,
            minHeight: "60vh",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            flex: 1,
          }}
        >
          <ShoppingCartIcon
            sx={{
              fontSize: 80,
              ...theme?.typography?.body,
              opacity: 0.3,
              mb: 2,
            }}
          />
          <AppTypography
            variant="h4"
            style={theme?.typography?.heading}
            fontWeight={600}
            fontSize="2rem"
            sx={{ mb: 2 }}
          >
            Your cart is empty
          </AppTypography>
          <AppTypography
            variant="body1"
            style={theme?.typography?.body}
            sx={{ mb: 4, opacity: 0.7 }}
          >
            Looks like you haven't added any items to your cart yet.
          </AppTypography>
          <AppButton
            variant="contained"
            size="large"
            onClick={() => navigate("/")}
            sx={{
              backgroundColor: theme?.app_button_color,
              color: theme?.app_button_text_color || "#fff",
              "&:hover": {
                backgroundColor: theme?.app_button_color,
                opacity: 0.9,
              },
            }}
          >
            Start Shopping
          </AppButton>
        </Box>
      </Container>
    );
  }

  // --- Confirmation Modal ---
  if (paymentVerified) {
    return (
      <Box
        sx={{
          minHeight: "100vh",
          width: "100vw",
          position: "fixed",
          top: 0,
          left: 0,
          zIndex: 2000,
          background: "rgba(0,0,0,0.6)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Box
          sx={{
            background: "#fff",
            borderRadius: 2,
            boxShadow: 6,
            p: 5,
            minWidth: 400,
            maxWidth: 400,
            textAlign: "center",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 2,
          }}
        >
          <CheckCircleIcon
            sx={{ fontSize: 64, color: theme?.app_button_color, mb: 1 }}
          />
          <AppTypography
            variant="h4"
            sx={{
              fontWeight: 700,
              fontSize: "1.2rem",
              textAlign: "center",
            }}
          >
            Order Placed Successfully!
          </AppTypography>

          <AppTypography
            variant="body1"
            sx={{ fontSize: "0.8rem", color: theme?.app_text }}
          >
            You will be redirected to the homepage in {redirectCountdown} sec
            {redirectCountdown !== 1 ? "s" : ""}.
          </AppTypography>

          <AppButton
            variant="contained"
            onClick={clearStoreAndNavigate}
            sx={{
              backgroundColor: theme?.app_button_color,
              color: theme?.app_button_text_color || "#fff",
              mt: 2,
              fontWeight: 600,
              fontSize: "1.1rem",
              borderRadius: 2,
              px: 4,
              py: 1.5,
            }}
          >
            Continue Shopping
          </AppButton>
        </Box>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        backgroundColor: theme?.store_background_color,
        py: 4,
        flex: 1,
        display: "flex",
        flexDirection: "column",
      }}
    >
      {paymentVerified && (
        <Popup
          image={popupimage}
          image2={popupimage2}
          title="Payment Successful!"
          subtitle={`Thank you for your order! You will be redirected to the homepage in ${redirectCountdown} seconds.`}
          buttonText="Go to Homepage"
          onButtonClick={clearStoreAndNavigate}
        />
      )}

      <Container maxWidth="lg">
        <Box sx={{ mb: 4 }}>
          <AppTypography
            variant="h3"
            color={theme?.text_color}
            sx={{ mb: 2, fontWeight: 600, fontSize: "1.6rem" }}
          >
            Checkout
          </AppTypography>
          <Stepper activeStep={activeStep} alternativeLabel>
            {steps.map((step, index) => (
              <Step key={step.label}>
                <StepLabel
                  StepIconComponent={() => step.icon}
                  sx={{
                    "& .MuiStepLabel-label": {
                      color: theme?.text_color,
                      fontWeight: 500,
                      fontSize: "0.9rem",
                    },
                  }}
                >
                  {step.label}
                </StepLabel>
              </Step>
            ))}
          </Stepper>
        </Box>

        <Grid container spacing={4}>
          {/* Left Column - Shipping Address & Payment */}
          <Grid item xs={12} md={7}>
            {/* Shipping Address Section */}
            <StyledCard
              sx={{
                mb: 3,
                backgroundColor: "#FFFFFF !important",
                border: "1px solid #E5E7EB",
                boxShadow: "0 1px 3px rgba(0, 0, 0, 0.08)",
                borderRadius: "14px",
              }}
            >
              <CardContent
                sx={{
                  p: 3,
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    mb: 1,
                    flexWrap: "wrap",
                    gap: 1,
                  }}
                >
                  <AppTypography
                    variant="h5"
                    color={theme?.text_color}
                    sx={{ fontWeight: 600, fontSize: "1.1rem" }}
                  >
                    {(hasAddresses || isLoadingAddressSection) &&
                      !displayEditForm
                      ? "Select Delivery Address"
                      : "Shipping Address"}
                  </AppTypography>
                  {displayEditForm && (
                    <IconButton
                      onClick={editForm}
                      sx={{ color: theme?.app_button_color }}
                    >
                      <EditIcon />
                    </IconButton>
                  )}
                  {hasAddresses && !displayEditForm && userId && (
                    <AppButton
                      variant="outlined"
                      size="small"
                      startIcon={<Add />}
                      onClick={() => {
                        setEditAddressInCheckout(null);
                        setAddressModalOpen(true);
                      }}
                      sx={{
                        backgroundColor: "#FFFFFF",
                        color: theme?.app_button_color || "#9810FA",
                        borderColor: theme?.app_button_color || "#9810FA",
                        borderRadius: "12px",
                        textTransform: "none",
                        fontSize: "14px",
                        fontWeight: 500,
                        px: 1.5,
                        py: 0.5,
                        "&:hover": {
                          backgroundColor: "rgba(152, 16, 250, 0.04)",
                          borderColor:
                            theme?.app_hover_button_color || "#8B0EE5",
                          color: theme?.app_hover_button_color || "#8B0EE5",
                        },
                      }}
                    >
                      Add New Address
                    </AppButton>
                  )}
                </Box>

                {!displayEditForm &&
                  !hasAddresses &&
                  !isLoadingAddressSection && (
                    <Box sx={{ mb: 3 }}>
                      <AppTypography
                        variant="body2"
                        color={theme?.app_grey}
                        sx={{
                          fontSize: "0.875rem",
                          fontStyle: "italic",
                          opacity: 0.8,
                        }}
                      >
                        Please enter the address where you would like your order
                        to be delivered.
                      </AppTypography>
                    </Box>
                  )}

                {!displayEditForm ? (
                  isLoadingAddressSection ? (
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        minHeight: 200,
                        py: 4,
                      }}
                    >
                      <CircularProgress
                        sx={{ color: theme?.app_button_color || "#9810FA" }}
                      />
                    </Box>
                  ) : hasAddresses ? (
                    // Address selection UI
                    <Box>
                      <Grid container spacing={2} sx={{ mb: 3 }}>
                        {savedAddresses.map((addr) => {
                          const isHome =
                            (addr.address_type || "home") === "home";
                          const isSelected = selectedAddressId === addr.id;
                          return (
                            <Grid item xs={12} key={addr.id}>
                              <Paper
                                onClick={() => {
                                  setSelectedAddressId(addr.id);
                                  setAddress((prev) => ({
                                    ...prev,
                                    ...mapApiAddressToCheckoutState(addr),
                                  }));
                                }}
                                sx={{
                                  p: 2.5,
                                  minHeight: "auto",
                                  borderRadius: "12px",
                                  cursor: "pointer",
                                  border: isSelected
                                    ? "2px solid"
                                    : "1px solid #E5E7EB",
                                  borderColor: isSelected
                                    ? theme?.app_button_color || "#9810FA"
                                    : "#E5E7EB",
                                  position: "relative",
                                  "&:hover": {
                                    borderColor: theme?.app_button_color,
                                  },
                                }}
                              >
                                <Box
                                  sx={{
                                    display: "flex",
                                    alignItems: "flex-start",
                                    gap: 1.5,
                                    minWidth: 0,
                                  }}
                                >
                                  <Box
                                    sx={{
                                      width: 40,
                                      height: 40,
                                      borderRadius: "10px",
                                      backgroundColor: isHome
                                        ? "rgba(33, 150, 243, 0.12)"
                                        : "rgba(156, 39, 176, 0.12)",
                                      display: "flex",
                                      alignItems: "center",
                                      justifyContent: "center",
                                    }}
                                  >
                                    {isHome ? (
                                      <HomeOutlined
                                        sx={{
                                          fontSize: 22,
                                          color: isHome ? "#2196F3" : "#9C27B0",
                                        }}
                                      />
                                    ) : (
                                      <BusinessOutlined
                                        sx={{
                                          fontSize: 22,
                                          color: "#9C27B0",
                                        }}
                                      />
                                    )}
                                  </Box>
                                  <Box sx={{ flex: 1 }}>
                                    <Box
                                      sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        flexWrap: "wrap",
                                        gap: 1,
                                        mb: 0.5,
                                        minWidth: 0,
                                      }}
                                    >
                                      <AppTypography
                                        sx={{
                                          fontWeight: 600,
                                          fontSize: "14px",
                                          color: theme?.text_color,
                                          wordBreak: "break-word",
                                          overflowWrap: "anywhere",
                                        }}
                                      >
                                        {isHome
                                          ? "Home Address"
                                          : "Work Address"}
                                      </AppTypography>
                                      {addr.is_default ? (
                                        <Box
                                          sx={{
                                            px: 1.5,
                                            py: 0.35,
                                            borderRadius: "20px",
                                            backgroundColor:
                                              theme?.app_button_color
                                                ? `${theme.app_button_color}20`
                                                : "rgba(152, 16, 250, 0.15)",
                                            color:
                                              theme?.app_button_color ||
                                              "#9810FA",
                                            fontSize: "12px",
                                            fontWeight: 500,
                                          }}
                                        >
                                          Default
                                        </Box>
                                      ) : null}
                                    </Box>
                                    <AppTypography
                                      variant="body2"
                                      sx={{
                                        fontSize: "13px",
                                        color: theme?.app_grey,
                                        mb: 1.5,
                                        wordBreak: "break-word",
                                        overflowWrap: "anywhere",
                                        lineHeight: 1.35,
                                      }}
                                    >
                                      {`${addr.first_name || ""} ${addr.last_name || ""}`.trim() ||
                                        addr.name}{" "}
                                      • {addr.phone_number}
                                    </AppTypography>
                                    <AppTypography
                                      variant="body2"
                                      sx={{
                                        fontSize: "13px",
                                        color: theme?.app_grey,
                                        wordBreak: "break-word",
                                        overflowWrap: "anywhere",
                                        lineHeight: 1.35,
                                      }}
                                    >
                                      {addr.address_line_1}
                                      {addr.address_line_2 &&
                                        `, ${addr.address_line_2}`}
                                      , {addr.city}, {addr.state} {addr.zipcode}
                                    </AppTypography>
                                    <Box
                                      component="span"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setEditAddressInCheckout(addr);
                                        setAddressModalOpen(true);
                                      }}
                                      sx={{
                                        display: "inline-flex",
                                        alignItems: "center",
                                        gap: 0.5,
                                        mt: 1.5,
                                        fontSize: "13px",
                                        color: "#6B7280",
                                        cursor: "pointer",
                                        "&:hover": {
                                          color:
                                            theme?.app_button_color ||
                                            "#9810FA",
                                        },
                                      }}
                                    >
                                      <EditIcon sx={{ fontSize: 16 }} />
                                      Edit
                                    </Box>
                                  </Box>
                                  {isSelected && (
                                    <CheckCircleIcon
                                      sx={{
                                        color:
                                          theme?.app_button_color || "#9810FA",
                                        fontSize: 24,
                                      }}
                                    />
                                  )}
                                </Box>
                              </Paper>
                            </Grid>
                          );
                        })}
                      </Grid>
                      <StyledButton
                        variant="contained"
                        fullWidth
                        onClick={handleContinueWithSelectedAddress}
                        disabled={!selectedAddressId}
                        sx={{
                          backgroundColor: theme?.app_button_color,
                          color: theme?.app_button_text_color,
                          fontWeight: 500,
                          borderRadius: 2,
                          fontSize: "14px",
                        }}
                      >
                        Continue with Selected Address
                      </StyledButton>
                    </Box>
                  ) : (
                    // Address Form
                    <Box component="form" onSubmit={handleFormSubmit}>
                      <Grid container spacing={3}>
                        <Grid item xs={12} sm={6}>
                          <StyledTextField
                            fullWidth
                            label="First Name"
                            name="first_name"
                            value={address.first_name}
                            onChange={handleChange}
                            required
                            variant="outlined"
                            sx={{ mb: 2, fontSize: "0.9rem" }}
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <StyledTextField
                            fullWidth
                            label="Last Name"
                            name="last_name"
                            value={address.last_name}
                            onChange={handleChange}
                            required
                            variant="outlined"
                            sx={{ mb: 2 }}
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <StyledTextField
                            fullWidth
                            label="Email"
                            name="email"
                            type="email"
                            value={address.email}
                            onChange={handleChange}
                            required
                            variant="outlined"
                            sx={{ mb: 2 }}
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <StyledTextField
                            fullWidth
                            label="Phone"
                            name="phone"
                            value={address.phone}
                            onChange={handleChange}
                            required
                            variant="outlined"
                            sx={{ mb: 2 }}
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <StyledTextField
                            fullWidth
                            label="Address Line 1"
                            name="address1"
                            value={address.address1}
                            onChange={handleChange}
                            required
                            variant="outlined"
                            sx={{ mb: 2 }}
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <StyledTextField
                            fullWidth
                            label="Address Line 2"
                            name="address2"
                            value={address.address2}
                            onChange={handleChange}
                            variant="outlined"
                            sx={{ mb: 2 }}
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <StyledTextField
                            fullWidth
                            label="Address Line 3"
                            name="address3"
                            value={address.address3}
                            onChange={handleChange}
                            variant="outlined"
                            sx={{ mb: 2 }}
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <StyledTextField
                            fullWidth
                            label="Landmark"
                            name="landmark"
                            value={address.landmark}
                            onChange={handleChange}
                            variant="outlined"
                            sx={{ mb: 2 }}
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <StyledTextField
                            fullWidth
                            label="City"
                            name="city"
                            value={address.city}
                            onChange={handleChange}
                            required
                            variant="outlined"
                            sx={{ mb: 2 }}
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <StyledTextField
                            fullWidth
                            label="State"
                            name="state"
                            value={address.state}
                            onChange={handleChange}
                            required
                            variant="outlined"
                            sx={{ mb: 2 }}
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <StyledTextField
                            fullWidth
                            label="Country"
                            name="country"
                            value={address.country}
                            onChange={handleChange}
                            required
                            variant="outlined"
                            sx={{ mb: 2 }}
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <StyledTextField
                            fullWidth
                            label="Postal Code"
                            name="pincode"
                            value={address.pincode}
                            onChange={handleChange}
                            required
                            variant="outlined"
                            sx={{ mb: 2 }}
                          />
                        </Grid>
                      </Grid>
                      <Box sx={{ mt: 3 }}>
                        <StyledButton
                          type="submit"
                          variant="contained"
                          fullWidth
                          disabled={
                            isCreatingUnifiedShipment || isVerifyingUser
                          }
                          sx={{
                            backgroundColor: theme?.app_button_color,
                            color: theme?.app_button_text_color,
                            fontWeight: 500,
                            borderRadius: 2,
                            fontSize: "14px",
                          }}
                        >
                          {isVerifyingUser
                            ? "Verifying..."
                            : "Continue to Payment"}
                        </StyledButton>
                      </Box>
                    </Box>
                  )
                ) : (
                  // Address Display
                  <AddressDisplayCard>
                    <Box sx={{ mb: 2 }}>
                      <AppTypography
                        variant="h6"
                        color={theme?.app_text}
                        sx={{ fontWeight: 600, mb: 1, fontSize: "1rem" }}
                      >
                        {address.first_name} {address.last_name}
                      </AppTypography>
                      <AppTypography
                        variant="body1"
                        color={theme?.app_text}
                        sx={{ fontSize: "0.9rem" }}
                      >
                        {address.address1}
                        {address.address2 && `, ${address.address2}`}
                        {address.address3 && `, ${address.address3}`}
                      </AppTypography>
                      <AppTypography
                        variant="body1"
                        color={theme?.app_text}
                        sx={{ fontSize: "0.9rem" }}
                      >
                        {address.city}, {address.state} {address.pincode}
                      </AppTypography>
                      <AppTypography
                        variant="body1"
                        color={theme?.app_text}
                        sx={{ fontSize: "0.9rem" }}
                      >
                        {address.country}
                      </AppTypography>
                      <AppTypography
                        variant="body1"
                        color={theme?.app_text}
                        sx={{ fontSize: "0.9rem" }}
                      >
                        Phone: {address.phone}
                      </AppTypography>
                      <AppTypography
                        variant="body1"
                        color={theme?.app_text}
                        sx={{ fontSize: "0.9rem" }}
                      >
                        Email: {address.email}
                      </AppTypography>
                    </Box>
                  </AddressDisplayCard>
                )}
              </CardContent>
            </StyledCard>

            {/* Payment Section */}
            {displayEditForm && (
              <StyledCard
                sx={{
                  backgroundColor: "#FFFFFF !important",
                  border: "1px solid #E5E7EB",
                  boxShadow: "0 1px 3px rgba(0, 0, 0, 0.08)",
                  borderRadius: "14px",
                }}
              >
                <CardContent sx={{ p: 3 }}>
                  <AppTypography
                    variant="h5"
                    color={theme?.text_color}
                    sx={{ fontWeight: 600, fontSize: "1.1rem" }}
                  >
                    Payment
                  </AppTypography>
                  <Box sx={{ textAlign: "center", py: 4 }}>
                    {/* <PaymentIcon
                      sx={{
                        fontSize: 64,
                        color: theme?.app_button_color,
                        mb: 2,
                      }}
                    /> */}
                    <AppTypography
                      variant="body1"
                      color={theme?.app_text}
                      sx={{ mb: 3, opacity: 0.7, fontSize: "0.9rem" }}
                    >
                      Your payment will be processed securely through Razorpay
                    </AppTypography>
                    <StyledButton
                      variant="contained"
                      fullWidth
                      onClick={handleSubmit}
                      disabled={
                        isCreatingUnifiedShipment ||
                        isPlacingCheckout ||
                        isCreatingOrder ||
                        isVerifyingPayment ||
                        isRecoveringPayment ||
                        isProcessingPayment ||
                        isConfirmingOrder
                      }
                    >
                      {isConfirmingOrder
                        ? "Confirming your order..."
                        : isCreatingUnifiedShipment ||
                            isPlacingCheckout ||
                            isCreatingOrder ||
                            isVerifyingPayment ||
                            isRecoveringPayment ||
                            isProcessingPayment
                          ? "Processing..."
                          : `Pay ${String.fromCodePoint(storeInfo?.store?.currency_symbol || 0x20b9)}${totalAfterPoints.toFixed(2)}`}
                    </StyledButton>
                  </Box>
                </CardContent>
              </StyledCard>
            )}
          </Grid>

          {/* Right Column - Order Summary (3 cards) */}
          <Grid item xs={12} md={5}>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: 2,
                position: "sticky",
                top: 80,
              }}
            >
              {/* Card 1: Your Items */}
              <Box
                sx={{
                  backgroundColor: "#FFFFFF !important",
                  border: "1px solid #E5E7EB",
                  boxShadow: "0 1px 3px rgba(0, 0, 0, 0.08)",
                  borderRadius: "14px",
                  p: 2.5,
                }}
              >
                <AppTypography
                  variant="h6"
                  sx={{
                    fontWeight: 600,
                    fontSize: "16px",
                    color: "#101828",
                    mb: 2,
                  }}
                >
                  Your Items
                </AppTypography>
                {cart.map((item, index) => (
                  <Box
                    key={`${item.pack_id ?? "nopack"}-${item.variant_id}-${index}`}
                    sx={{
                      display: "flex",
                      alignItems: "flex-start",
                      gap: 2,
                      mb: 2,
                    }}
                  >
                    <Box
                      sx={{
                        width: 68,
                        height: 68,
                        flexShrink: 0,
                        border: "1px solid #A7A9AD",
                        borderRadius: "10px",
                        p: 0.5,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        overflow: "hidden",
                      }}
                    >
                      <img
                        src={item.image_url}
                        alt={item.name}
                        style={{
                          width: "100%",
                          height: "100%",
                          objectFit: "contain",
                          borderRadius: 6,
                        }}
                      />
                    </Box>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <AppTypography
                        variant="body1"
                        sx={{
                          fontWeight: 500,
                          fontSize: "14px",
                          color: "#101828",
                          lineHeight: 1.3,
                          wordBreak: "break-word",
                        }}
                      >
                        {item?.name}
                      </AppTypography>
                      <Box sx={{ mt: 0.5 }}>
                        {item.selected_size && (
                          <AppTypography
                            variant="body2"
                            sx={{
                              fontSize: "13px",
                              color: "#6A7282",
                              fontWeight: 400,
                            }}
                          >
                            {isOnesize(item.selected_size)
                              ? ""
                              : `Size: ${item.selected_size},`}
                            {item.selected_color &&
                              ` Color: ${item.selected_color}`}
                          </AppTypography>
                        )}
                        <AppTypography
                          variant="body2"
                          sx={{
                            fontSize: "13px",
                            color: "#101828",
                            fontWeight: 400,
                          }}
                        >
                          Qty: {item.selected_quantity}
                        </AppTypography>
                      </Box>
                    </Box>
                    <AppTypography
                      variant="body1"
                      sx={{
                        fontWeight: 500,
                        fontSize: "15px",
                        color: "#101828",
                        flexShrink: 0,
                      }}
                    >
                      {String.fromCodePoint(
                        storeInfo?.store?.currency_symbol || 0x20b9,
                      )}
                      {(
                        item.selected_quantity * (item.selling_price || 0)
                      ).toFixed(2)}
                    </AppTypography>
                  </Box>
                ))}
              </Box>

              {/* Card 2: Final Summary */}
              <Box
                sx={{
                  backgroundColor: "#FFFFFF !important",
                  borderRadius: "14px",
                  boxShadow: "0 1px 3px rgba(0, 0, 0, 0.08)",
                  p: 2.5,
                  border: "1px solid #E5E7EB",
                }}
              >
                <AppTypography
                  variant="h6"
                  sx={{
                    fontWeight: 500,
                    fontSize: "15px",
                    color: "#101828",
                    mb: 2,
                  }}
                >
                  Final Summary
                </AppTypography>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    mb: 1,
                  }}
                >
                  <AppTypography
                    variant="body2"
                    sx={{ fontSize: "14px", color: "#364153" }}
                  >
                    Items Subtotal
                  </AppTypography>
                  <AppTypography
                    variant="body2"
                    sx={{ fontSize: "14px", color: "#364153" }}
                  >
                    {String.fromCodePoint(
                      storeInfo?.store?.currency_symbol || 0x20b9,
                    )}
                    {totalPrice?.toFixed(2)}
                  </AppTypography>
                </Box>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    mb: 1,
                  }}
                >
                  <AppTypography
                    variant="body2"
                    sx={{ fontSize: "14px", color: "#364153" }}
                  >
                    Shipping
                  </AppTypography>
                  <AppTypography
                    variant="body2"
                    sx={{ fontSize: "14px", color: "#364153" }}
                  >
                    Free
                  </AppTypography>
                </Box>
                {userId && points > 0 && (
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      mb: 1,
                      backgroundColor: "#F3F4F6",
                      p: 1,
                      pl: 2,
                      pr: 2,
                      borderRadius: "10px",
                    }}
                  >
                    <FormControlLabel
                      sx={{
                        mr: 0,
                        mb: 0,
                        "& .MuiFormControlLabel-label": { mb: 0 },
                      }}
                      control={
                        <Checkbox
                          checked={usePoints}
                          onChange={(e) => setUsePoints(e.target.checked)}
                          size="small"
                          sx={{
                            color: theme?.app_button_color,
                            "&.Mui-checked": {
                              color: theme?.app_button_color,
                            },
                          }}
                        />
                      }
                      label={
                        <AppTypography
                          variant="body2"
                          sx={{
                            fontSize: "14px",
                            fontWeight: 500,
                            color: "#101828",
                            marginBottom: "0px !important",
                          }}
                        >
                          Use Available Points ({points} pts)
                        </AppTypography>
                      }
                    />
                  </Box>
                )}
                {userId && usePoints && pointsDiscount > 0 && (
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      mb: 1,
                    }}
                  >
                    <AppTypography
                      variant="body2"
                      sx={{ fontSize: "14px", color: "#364153" }}
                    >
                      Discount
                    </AppTypography>
                    <AppTypography
                      variant="body2"
                      sx={{ fontSize: "14px", color: "#364153" }}
                    >
                      -{Math.round(pointsValue)} pts
                    </AppTypography>
                  </Box>
                )}
                <Box
                  sx={{
                    borderTop: "1px solid #E5E7EB",
                    pt: 1.5,
                    mt: 1.5,
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <AppTypography
                    variant="h6"
                    sx={{
                      fontWeight: 400,
                      fontSize: "14px",
                      color: "#101828",
                    }}
                  >
                    Final Total
                  </AppTypography>
                  <AppTypography
                    variant="h6"
                    sx={{
                      fontWeight: 400,
                      fontSize: "18px",
                      color: "#101828",
                    }}
                  >
                    {String.fromCodePoint(
                      storeInfo?.store?.currency_symbol || 0x20b9,
                    )}
                    {totalAfterPoints.toFixed(2)}
                  </AppTypography>
                </Box>
                {/* <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 0.5,
                    mt: 2,
                    pt: 1.5,
                  }}
                >
                  <LockIcon sx={{ fontSize: "14px", color: "#6B7280" }} />
                  <AppTypography
                    variant="caption"
                    sx={{ fontSize: "11px", color: "#6B7280" }}
                  >
                    Secure checkout powered by Swageazy
                  </AppTypography>
                </Box> */}
              </Box>
            </Box>
          </Grid>
        </Grid>
      </Container>

      <AddressModal
        open={addressModalOpen}
        onClose={() => {
          setAddressModalOpen(false);
          setEditAddressInCheckout(null);
        }}
        onSubmit={handleAddressModalSubmit}
        editAddress={editAddressInCheckout}
        userEmail={userData?.email}
      />

      {/* OTP Verification Popup */}
      {showOTPPopup && (
        <Box
          sx={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 2000,
            backdropFilter: "blur(4px)",
          }}
        >
          <Card
            variant="elevated"
            sx={{
              width: "100%",
              maxWidth: 420,
              p: { xs: 3, sm: 4 },
              borderRadius: 3,
              boxShadow: "0 8px 32px 0 rgba(0,0,0,0.10)",
              background: "#fff",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 3,
              mx: 2,
            }}
          >
            {/* Header */}
            <Box sx={{ textAlign: "center" }}>
              <AppTypography
                variant="h3"
                style={theme?.typography?.heading}
                fontWeight={700}
                fontSize={{ xs: "1.75rem", sm: "2rem", md: "2.2rem" }}
                textAlign="center"
                mb={1}
              >
                Enter OTP
              </AppTypography>
              <AppTypography
                variant="body1"
                style={theme?.typography?.body}
                fontSize={{ xs: "0.9rem", sm: "1rem", md: "1.1rem" }}
                textAlign="center"
                sx={{ opacity: 0.9 }}
              >
                {isUserRegistered
                  ? "We've sent a verification code to your email"
                  : "We've created your account and sent a verification code to your email"}
              </AppTypography>
            </Box>

            {/* OTP Form */}
            <Box
              component="form"
              autoComplete="off"
              noValidate
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: 3,
                width: "100%",
              }}
            >
              {/* OTP Input */}
              <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                <AppTypography
                  variant="body2"
                  style={theme?.typography?.body}
                  fontSize="0.875rem"
                  textAlign="center"
                  sx={{ opacity: 0.7 }}
                >
                  Enter the 6-character code
                </AppTypography>
                <OTPInput
                  value={otp}
                  onChange={(value) => setOtp(value.toUpperCase())}
                  disabled={isVerifyingOTP}
                />
              </Box>

              {/* Action Buttons */}
              <Box sx={{ display: "flex", gap: 2, justifyContent: "center" }}>
                <AppButton
                  variant="outlined"
                  onClick={() => {
                    setShowOTPPopup(false);
                    setOtp("");
                  }}
                  disabled={isVerifyingOTP}
                  sx={{
                    borderColor: theme?.app_button_color,
                    backgroundColor: theme?.app_button_color,
                    color: theme?.app_button_text_color,
                    "&:hover": {
                      borderColor: theme?.app_hover_button_color,
                      backgroundColor: theme?.app_button_color,
                      color: theme?.app_button_text_color,
                    },
                  }}
                >
                  Cancel
                </AppButton>

                <AppButton
                  variant="contained"
                  onClick={handleOTPVerification}
                  disabled={!otp || otp.length !== 6 || isVerifyingOTP}
                  sx={{
                    backgroundColor: theme?.app_button_color,
                    color: theme?.app_button_text_color,
                    "&:hover": {
                      backgroundColor: theme?.app_hover_button_color,
                    },
                  }}
                >
                  {isVerifyingOTP ? "Verifying..." : "Verify OTP"}
                </AppButton>
              </Box>

              {/* Resend OTP */}
              <Box sx={{ textAlign: "center" }}>
                <AppTypography
                  variant="body2"
                  style={theme?.typography?.body}
                  fontSize="0.875rem"
                  textAlign="center"
                  sx={{ opacity: 0.7 }}
                >
                  Didn't receive the code?{" "}
                  <AppTypography
                    component="button"
                    type="button"
                    onClick={handleResendOTP}
                    disabled={isSendingOTP}
                    style={theme?.typography?.link}
                    fontSize="0.875rem"
                    fontWeight={600}
                    sx={{
                      textDecoration: "none",
                      cursor: isSendingOTP ? "not-allowed" : "pointer",
                      border: "none",
                      background: "none",
                      "&:hover": {
                        textDecoration: isSendingOTP ? "none" : "underline",
                      },
                      opacity: isSendingOTP ? 0.5 : 1,
                    }}
                  >
                    {isSendingOTP ? "Sending..." : "Resend OTP"}
                  </AppTypography>
                </AppTypography>
              </Box>
            </Box>
          </Card>
        </Box>
      )}
    </Box>
  );
};

export default Checkout;
